import { ReactNode, useId } from 'react'

import { Button } from 'core-components/library/button'
import { Loader } from 'core-components/library/loader'

import { useScreeningResult } from '../../api/useCompetitorBadActorReview'
import { IScreeningVerdict, TScreening } from '../../model/types'
import { COMPETITOR_BAD_ACTOR_REVIEW_TEXT as TEXT, tierText, useReviewText } from '../translations'

import { ScreeningMatchesList } from './ScreeningMatchesList'
import { ScreeningRerun } from './ScreeningRerun'

import styles from './CompetitorBadActorReview.module.scss'

interface ICompetitorBadActorReviewProps {
  requestId: number
  screening: TScreening
}

const FoundResult = ({ verdict }: { verdict: IScreeningVerdict }) => {
  const text = useReviewText()
  const { matches } = verdict
  const tierLabel = tierText(verdict.tier)

  return (
    <>
      <p className={styles.summary}>
        <span className={styles.outcome}>
          {text(TEXT.outcome, { tier: tierLabel === null ? verdict.tier : text(tierLabel) })}
        </span>
        <span>{text(TEXT.resultMatchedCount, { total: matches.total })}</span>
        {matches.entries.length < matches.total && (
          <span>
            {text(TEXT.resultShowing, { shown: matches.entries.length, total: matches.total })}
          </span>
        )}
      </p>
      {matches.entries.length > 0 && (
        <ScreeningMatchesList
          comparison="stored"
          entries={matches.entries}
          caption={text(TEXT.matchedCaption)}
          regionLabel={text(TEXT.matchedRegionLabel)}
        />
      )}
    </>
  )
}

export const CompetitorBadActorReview = ({
  requestId,
  screening,
}: ICompetitorBadActorReviewProps) => {
  const text = useReviewText()
  const headingId = useId()
  const result = useScreeningResult(requestId, screening)

  const resultArea = (): { status: string; details: ReactNode } => {
    if (result.isPending) {
      return { status: text(TEXT.resultLoading), details: <Loader /> }
    }

    if (result.isError) {
      return {
        status: result.isFetching ? text(TEXT.resultLoading) : '',
        details: (
          <>
            <p role="alert" key={result.errorUpdatedAt}>
              {text(TEXT.resultError)}
            </p>
            <Button variant="outlined" onClick={() => result.refetch()}>
              {text(TEXT.retry)}
            </Button>
          </>
        ),
      }
    }

    switch (result.data.kind) {
      case 'none':
        return { status: text(TEXT.resultNone), details: null }
      case 'refused':
        return { status: '', details: <p role="alert">{text(TEXT.refused)}</p> }
      case 'found':
        return { status: '', details: <FoundResult verdict={result.data.verdict} /> }
    }
  }

  const { status, details } = resultArea()
  const refused = result.isSuccess && result.data.kind === 'refused'

  return (
    <section className={styles.root} aria-labelledby={headingId} aria-busy={result.isPending}>
      <div>
        <h4 id={headingId} className={styles.heading}>
          {text(screening === 'competitors' ? TEXT.headingCompetitors : TEXT.headingBadActors)}
        </h4>
        <p role="status" className={styles.status}>
          {status}
        </p>
      </div>
      {details}
      {!result.isPending && !refused && (
        <ScreeningRerun requestId={requestId} screening={screening} />
      )}
    </section>
  )
}
